import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../lib/utils";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "primary"
    | "secondary"
    | "tertiary" // "inverted" in Figma, can this be replaced with another of the existing variants?
    | "outline"
    | "ghost" // "noBg" in Figma
    | "white"
    | "disabled";
  size?:
    | "default"
    | "icon"
    | "icon-tiny"
    | "always-small" // 35 results in 24 files
    | "always-big" // only used in one place
    | "large" //TODO: used in 5 places, was a special request from Jose to have a larger AddToCart on pdp.
    | "navigation"; //TODO: placeholder for now, remove when we implement the new button component
  asChild?: boolean;
  noPaddingInline?: boolean; //TODO: placeholder for now, remove when we implement the new button component
  babylist?: boolean; //TODO: placeholder for now, remove when we implement the new button component
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "default",
      asChild = false,
      noPaddingInline = false,
      babylist = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        className={cn(
          // Base styles
          "bb-text-button inline-flex items-center justify-center whitespace-nowrap",
          "box-border gap-2 rounded-full border",
          "transition-colors disabled:cursor-not-allowed",
          "[&_svg]:size-4 [&_svg]:shrink-0",
          "tab-focus",

          // Variant styles
          {
            "bg-rusty-purple hover:bg-rusty-brown active-fine:bg-rusty-purple active-coarse:bg-rusty-brown border-transparent text-white":
              variant === "primary",
            "bg-gray-2 hover:bg-gray-4 active-fine:bg-gray-2 active-coarse:bg-gray-4 border-transparent text-white":
              variant === "secondary",
            "text-gray-4 border-gray-border hover:bg-gray-4 active-fine:border-gray-border active-fine:text-gray-4 active-coarse:bg-gray-4 active-coarse:border-transparent active-coarse:text-white active-fine:bg-white bg-white hover:border-transparent hover:text-white":
              variant === "tertiary",
            "text-gray-4 border-gray-border active-coarse:bg-white active-fine:bg-transparent bg-transparent hover:bg-white":
              variant === "outline",
            "outline-gray-border text-gray-4 hover:border-gray-border active-coarse:border-gray-border active-fine:border-transparent border-transparent bg-transparent":
              variant === "ghost",
            "text-gray-4 hover:border-gray-border active-coarse:border-gray-border active-fine:border-transparent border-transparent bg-white":
              variant === "white",
            "bg-gray-border text-gray-5 border-transparent":
              variant === "disabled",
          },

          // Size styles
          {
            "h-10 px-3 py-2 lg:h-12 lg:px-5 lg:py-3": size === "default",
            "h-10 px-3 py-2": size === "always-small",
            "h-12 px-5 py-3": size === "always-big",
            "h-12 px-5 py-3 lg:h-14 lg:px-6 lg:py-4": size === "large",
            "size-10 lg:size-12": size === "icon",
            "size-4 [&_svg]:size-3": size === "icon-tiny",
            "h-10 px-2 py-2 text-xs leading-4 lg:h-12": size === "navigation",
          },

          // Padding styles
          {
            "px-0": noPaddingInline,
          },

          // Babylist styles
          {
            "[font-size:0.625rem]": babylist,
          },

          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button };
